import { Injectable } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { Actions } from '@ngrx/effects';
import { createEffect } from '@ngrx/effects';
import { toErrorMessage } from 'app/modules/main/helper/error-mapper';
import { AdvertiserAccount } from 'app/modules/main/models/account';
import { PublisherAccount } from 'app/modules/main/models/account';
import { ErrorMessage } from 'app/modules/main/models/authorization';
import { Authorization } from 'app/modules/main/models/authorization';
import { UserAuthorizer } from 'app/modules/main/services/user-authorizer.service';
import { WindowService } from 'app/modules/main/services/window.service';
import { UserAuthorizationActions } from 'app/modules/main/store/user-authorization/user-authorization.actions';
import { environment } from 'environments/environment';
import { of } from 'rxjs';
import { first } from 'rxjs/operators';
import { switchMap } from 'rxjs/operators';
import { tap } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserAuthorizationEffects {

  constructor(
    private readonly actions: Actions,
    private readonly userAuthorizer: UserAuthorizer,
    private readonly windowService: WindowService,
  ) {
  }

  readonly authorizeUserForAdvertiserAccount = createEffect(
    () => this.actions.pipe(
      ofType(UserAuthorizationActions.userClaimingAuthorizationForAdvertiserAccount),
      map(action => action.account),
      switchMap((advertiserAccount: AdvertiserAccount) => {

        return this.userAuthorizer.authorizeUserForAccount(advertiserAccount)
          .pipe(
            first(),
            map(UserAuthorizationActions.userAuthorizedForAdvertiserAccount),
            catchError((error: any) => of(UserAuthorizationActions.userUnauthorizedForAdvertiserAccount(toErrorMessage(error)))),
          );
      }),
    )
  );

  readonly authorizeUserForPublisherAccount = createEffect(
    () => this.actions.pipe(
      ofType(UserAuthorizationActions.userClaimingAuthorizationForPublisherAccount),
      map(action => action.account),
      switchMap((publisherAccount: PublisherAccount) => {

        return this.userAuthorizer.authorizeUserForAccount(publisherAccount)
          .pipe(
            first(),
            map(UserAuthorizationActions.userAuthorizedForPublisherAccount),
            catchError((error: any) => of(UserAuthorizationActions.userUnauthorizedForPublisherAccount(toErrorMessage(error)))),
          );
      }),
    )
  );

  readonly refreshAuthorizationForAdvertiserAccount = createEffect(
    () => this.actions.pipe(
      ofType(UserAuthorizationActions.authorizationRefreshRequestedForAdvertiserAccount),
      map(action => action.authorization),
      switchMap((authorization: Authorization<AdvertiserAccount>) => {

        return this.userAuthorizer.refresh(authorization)
          .pipe(
            first(),
            map(UserAuthorizationActions.userAuthorizationRefreshedForAdvertiserAccount),
            catchError((error: any) => of(UserAuthorizationActions.userUnauthorizedForAdvertiserAccount(toErrorMessage(error)))),
          );
      }),
    )
  );

  readonly refreshAuthorizationForPublisherAccount = createEffect(
    () => this.actions.pipe(
      ofType(UserAuthorizationActions.authorizationRefreshRequestedForPublisherAccount),
      map(action => action.authorization),
      switchMap((authorization: Authorization<PublisherAccount>) => {

        return this.userAuthorizer.refresh(authorization)
          .pipe(
            first(),
            map(UserAuthorizationActions.userAuthorizationRefreshedForPublisherAccount),
            catchError((error: any) => of(UserAuthorizationActions.userUnauthorizedForPublisherAccount(toErrorMessage(error)))),
          );
      }),
    )
  );

  readonly rejectUnauthorizedAccess = createEffect(
    () => this.actions.pipe(
      ofType(
        UserAuthorizationActions.userUnauthorizedForAdvertiserAccount,
        UserAuthorizationActions.userUnauthorizedForPublisherAccount,
      ),
      map(action => action.error),
      tap((error?: string) => this.handleAuthorizationError(error)),
    ),
    { dispatch: false }
  );

  private handleAuthorizationError(
    error?: string
  ): void {

    if (!environment.production) {
      console.warn('Authorization failed: ', error);
    }

    if (error === ErrorMessage.NOT_AUTHENTICATED) {
      return;
    }

    if (error === ErrorMessage.NOT_AUTHORISED) {
      this.windowService.redirectToUnauthorizedPage();
    } else {
      this.windowService.redirectToErrorPage();
    }
  }

}
